import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
} from "react";
import {
  ActionIcon,
  Badge,
  Button,
  Collapse,
  Divider,
  Group,
  Loader,
  NumberInput,
  Paper,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useLazyQuery } from "@apollo/client/react";
import { notifications } from "@mantine/notifications";
import {
  IconBolt,
  IconCircleCheck,
  IconCircleX,
  IconClock,
  IconPlayerPlay,
  IconPlus,
  IconTrash,
  IconUsersGroup,
} from "@tabler/icons-react";
import { SIMULATE_SCRIPT_QUERY } from "../graphql/mutations";
import type {
  ScriptContextType,
  ScriptError,
  ScriptWarning,
} from "./ValidationPanel";
import { ScriptIssueList } from "./SharedRuleParts";
import {
  SimulationPresetParameterValueType,
  type SimulationContextOverridesInput,
  type SimulationKeyValueInput,
  type SimulationPresetParameterInput,
  type SimulationTeamOverrideInput,
} from "../../../../../../../gql/graphql";

export enum ScriptParameterType {
  INT = "INT",
  FLOAT = "FLOAT",
  STRING = "STRING",
  BOOLEAN = "BOOLEAN",
}

export interface ScriptParameterDefinition {
  name: string;
  type: ScriptParameterType;
  required: boolean;
  defaultValue: string | null;
  description: string | null;
}

export interface SimulatedEffect {
  type: string;
  description: string;
}

interface SimulateScriptResult {
  success: boolean;
  errors: ScriptError[];
  warnings: ScriptWarning[];
  effectsSummary: SimulatedEffect[];
  /**
   * Effects collected before the script threw. `null` on success; non-null
   * when the script failed mid-loop after emitting at least one effect.
   * NOT applied — for diagnostic display only.
   */
  partialEffectsSummary: SimulatedEffect[] | null;
  executionTimeMs: number;
  instructionsUsed: number;
}

/**
 * Mirrors `SimulateScriptInput` from the schema, with the deprecated
 * `parameters: String` JSON blob intentionally omitted. The console exclusively
 * uses the typed `overrides` shape going forward.
 */
interface SimulateScriptInput {
  script: string;
  context: ScriptContextType;
  overrides: SimulationContextOverridesInput | null;
  stepId: string | null;
}

interface SimulateScriptResponse {
  simulateScript: SimulateScriptResult;
}

interface SimulateScriptVariables {
  input: SimulateScriptInput;
}

interface SimulationPanelProps {
  script: string;
  context: ScriptContextType;
  stepId?: string;
  parametersSchema?: ScriptParameterDefinition[];
}

type ParameterValue = number | string | boolean;
type ParameterValues = Record<string, ParameterValue>;

/**
 * One key-value entry in a synthetic team's metadata bag, with a stable client
 * id so React reconciliation does not lose focus when the user edits keys.
 */
interface TeamMetadataEntryFormState {
  readonly id: string;
  key: string;
  value: string;
}

/**
 * In-memory editor state for a single `<TeamOverrideRow>`. Mirrors a subset of
 * `SimulationTeamOverrideInput`. The `id` is local-only (React reconciliation
 * key); it is not the synthetic team id sent to the backend. We currently only
 * surface `position` + `metadata` because that's what unblocks the
 * sort-by-metadata preset author. `customFields`, `rank`, and `status` will
 * land in a follow-up pass; until then the simulator keeps fixture defaults.
 */
interface TeamOverrideFormState {
  readonly id: string;
  position: number | null;
  metadata: TeamMetadataEntryFormState[];
}

/**
 * Per-row validation flags. We surface error states inline via Mantine input
 * `error` props rather than refusing to render. Submission is blocked
 * separately.
 */
interface TeamOverrideRowValidation {
  readonly emptyKeyEntryIds: ReadonlySet<string>;
  readonly duplicateKeyEntryIds: ReadonlySet<string>;
}

/**
 * Simulator-only fixture knobs sent inside `overrides` (`teamCount` / `bestOf`).
 * They size the synthetic step the simulator builds — they are NOT preset
 * parameters and should not appear in the preset's parameter schema.
 *
 * Both fields are optional; when omitted the backend applies its own defaults
 * (`teamCount = 8`, `bestOf = 1`). Empty inputs MUST stay empty: do not
 * pre-fill defaults visibly.
 *
 * Range constraints (enforced server-side, mirrored here for fast UX):
 *   - `teamCount` clamps to a multiple of 4 between 4 and 64
 *   - `bestOf`    clamps between 1 and 99
 */
interface SimulatorContextOverrides {
  readonly teamCount: number | null;
  readonly bestOf: number | null;
}

const TEAM_COUNT_MIN = 4;
const TEAM_COUNT_MAX = 64;
const TEAM_COUNT_STEP = 4;
const TEAM_COUNT_DEFAULT = 8;
const BEST_OF_MIN = 1;
const BEST_OF_MAX = 99;

const EMPTY_SIMULATOR_OVERRIDES: SimulatorContextOverrides = {
  teamCount: null,
  bestOf: null,
};

/**
 * Fresh React-key for a list item. `crypto.randomUUID()` is universally
 * available in modern browsers and the Vitest jsdom env, so no polyfill is
 * needed here.
 */
const newClientId = (): string => crypto.randomUUID();

const newMetadataEntry = (): TeamMetadataEntryFormState => ({
  id: newClientId(),
  key: "",
  value: "",
});

const newTeamOverrideRow = (): TeamOverrideFormState => ({
  id: newClientId(),
  position: null,
  metadata: [],
});

/**
 * Convert a `NumberInput` change value (which may be `""` / `number`) to a
 * nullable number. Empty input → `null`; finite number → number; anything else
 * (NaN, `string`) → `null`.
 */
const numberInputToNullable = (value: number | string): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  return null;
};

/**
 * Clamp a raw `team_count` input to `[TEAM_COUNT_MIN, TEAM_COUNT_MAX]` and
 * snap to the nearest multiple of `TEAM_COUNT_STEP`. Mantine's `NumberInput`
 * only enforces min/max/step on blur and step buttons — typed-in values reach
 * `onChange` verbatim, so we clamp on the way into state.
 */
const clampTeamCount = (value: number): number =>
  Math.min(
    TEAM_COUNT_MAX,
    Math.max(
      TEAM_COUNT_MIN,
      Math.round(value / TEAM_COUNT_STEP) * TEAM_COUNT_STEP,
    ),
  );

/**
 * Clamp a raw `best_of` input to `[BEST_OF_MIN, BEST_OF_MAX]` and truncate to
 * an integer. Same rationale as `clampTeamCount`.
 */
const clampBestOf = (value: number): number =>
  Math.min(BEST_OF_MAX, Math.max(BEST_OF_MIN, Math.trunc(value)));

const parseDefault = (def: ScriptParameterDefinition): ParameterValue => {
  switch (def.type) {
    case ScriptParameterType.INT: {
      if (def.defaultValue === null) return 0;
      const n = parseInt(def.defaultValue, 10);
      return Number.isNaN(n) ? 0 : n;
    }
    case ScriptParameterType.FLOAT: {
      if (def.defaultValue === null) return 0;
      const n = parseFloat(def.defaultValue);
      return Number.isNaN(n) ? 0 : n;
    }
    case ScriptParameterType.STRING:
      return def.defaultValue ?? "";
    case ScriptParameterType.BOOLEAN:
      return (
        def.defaultValue !== null && def.defaultValue.toLowerCase() === "true"
      );
  }
};

const buildInitialValues = (
  schema: ScriptParameterDefinition[] | undefined,
): ParameterValues => {
  if (!schema) return {};
  const result: ParameterValues = {};
  for (const def of schema) {
    result[def.name] = parseDefault(def);
  }
  return result;
};

/**
 * Map a console-side `ScriptParameterType` to the GraphQL discriminator for
 * `SimulationPresetParameterInput.type`. INT and FLOAT both collapse to
 * `NUMBER` because the backend value union has a single numeric slot
 * (`numberValue: Float`), and Lua truncates back to INT at the boundary.
 */
const toPresetParameterValueType = (
  type: ScriptParameterType,
): SimulationPresetParameterValueType => {
  switch (type) {
    case ScriptParameterType.INT:
    case ScriptParameterType.FLOAT:
      return SimulationPresetParameterValueType.Number;
    case ScriptParameterType.STRING:
      return SimulationPresetParameterValueType.String;
    case ScriptParameterType.BOOLEAN:
      return SimulationPresetParameterValueType.Boolean;
    default: {
      const _exhaustive: never = type;
      throw new Error(
        `Unhandled ScriptParameterType: ${String(_exhaustive)}`,
      );
    }
  }
};

/**
 * Project a single declared parameter + its current form value into the typed
 * `SimulationPresetParameterInput` shape. Only the field that matches `type`
 * is populated; the others stay `null` so the backend reads a single value
 * without ambiguity.
 */
const toSimulationPresetParameterInput = (
  def: ScriptParameterDefinition,
  value: ParameterValue | undefined,
): SimulationPresetParameterInput => {
  const type = toPresetParameterValueType(def.type);
  const base: SimulationPresetParameterInput = {
    name: def.name,
    type,
    stringValue: null,
    numberValue: null,
    booleanValue: null,
  };
  switch (type) {
    case SimulationPresetParameterValueType.Number:
      return {
        ...base,
        numberValue: typeof value === "number" ? value : 0,
      };
    case SimulationPresetParameterValueType.String:
      return {
        ...base,
        stringValue: typeof value === "string" ? value : "",
      };
    case SimulationPresetParameterValueType.Boolean:
      return {
        ...base,
        booleanValue: value === true,
      };
    default: {
      const _exhaustive: never = type;
      throw new Error(
        `Unhandled SimulationPresetParameterValueType: ${String(_exhaustive)}`,
      );
    }
  }
};

/**
 * Project a `TeamOverrideFormState` (UI state) into the typed
 * `SimulationTeamOverrideInput` shape sent to the backend. Empty metadata
 * entries (key === "") are filtered out — the validator already blocks
 * submission when any are present, but stripping them defensively keeps the
 * payload clean if any survive.
 *
 * `customFields`, `rank`, and `status` are intentionally omitted: this
 * iteration ships metadata-only because it's what unblocks the sort-by-
 * metadata script preview. They will be added in a follow-up pass.
 */
const toSimulationTeamOverrideInput = (
  row: TeamOverrideFormState,
): SimulationTeamOverrideInput => {
  const metadataEntries: SimulationKeyValueInput[] = row.metadata
    .filter((entry) => entry.key.length > 0)
    .map((entry) => ({ key: entry.key, value: entry.value }));
  // id/customFields/rank/status omitted — backend defaults apply (UI exposes metadata-only this iteration).
  // TODO: ship customFields/rank/status editor once metadata-only flow stabilizes.
  return {
    position: row.position,
    metadata: metadataEntries.length > 0 ? metadataEntries : null,
  };
};

/**
 * Compute per-row validation: which entries have an empty key, and which
 * collide with another non-empty key in the same row. Both conditions block
 * submission and surface inline as Mantine `error` props.
 */
const validateTeamRow = (
  row: TeamOverrideFormState,
): TeamOverrideRowValidation => {
  const emptyKeyEntryIds = new Set<string>();
  const duplicateKeyEntryIds = new Set<string>();
  const seen = new Map<string, string>();
  for (const entry of row.metadata) {
    if (entry.key.length === 0) {
      emptyKeyEntryIds.add(entry.id);
      continue;
    }
    const previousId = seen.get(entry.key);
    if (previousId !== undefined) {
      duplicateKeyEntryIds.add(entry.id);
      duplicateKeyEntryIds.add(previousId);
    } else {
      seen.set(entry.key, entry.id);
    }
  }
  return { emptyKeyEntryIds, duplicateKeyEntryIds };
};

interface EffectsListProps {
  readonly effects: ReadonlyArray<SimulatedEffect>;
  readonly variant: "success" | "partial";
}

/**
 * Vertical list of simulated effects. Rendered both for the successful run's
 * effect summary and for the partial-effects diagnostic shown when the
 * simulator threw mid-loop. The `partial` variant dims the row to signal that
 * none of those effects were applied.
 */
const EffectsList = ({
  effects,
  variant,
}: EffectsListProps): ReactElement | null => {
  if (effects.length === 0) return null;
  const dimmed = variant === "partial";
  return (
    <Stack gap={4}>
      {effects.map((e, i) => (
        <Group key={`${variant}-${i}`} gap={8} wrap="nowrap" align="start">
          <IconBolt
            size={14}
            color={
              dimmed
                ? "var(--mantine-color-gray-6)"
                : "var(--mantine-color-yellow-7)"
            }
          />
          <Text size="sm" c={dimmed ? "dimmed" : undefined}>
            <Text span fw={600}>
              {e.type}
            </Text>{" "}
            — {e.description}
          </Text>
        </Group>
      ))}
    </Stack>
  );
};

interface TeamOverrideRowProps {
  readonly row: TeamOverrideFormState;
  readonly index: number;
  readonly maxPosition: number;
  readonly validation: TeamOverrideRowValidation;
  readonly onChange: (next: TeamOverrideFormState) => void;
  readonly onRemove: () => void;
}

/**
 * Editor for a single synthetic team override. Renders inputs for `position`
 * and a key/value table for `metadata`. Validation flags surface inline via
 * Mantine `error` props; the parent panel blocks submission when any flag is
 * set.
 */
const TeamOverrideRow = ({
  row,
  index,
  maxPosition,
  validation,
  onChange,
  onRemove,
}: TeamOverrideRowProps): ReactElement => {
  const updateMetadataEntry = (
    entryId: string,
    patch: Partial<Pick<TeamMetadataEntryFormState, "key" | "value">>,
  ): void => {
    onChange({
      ...row,
      metadata: row.metadata.map((entry) =>
        entry.id === entryId ? { ...entry, ...patch } : entry,
      ),
    });
  };

  const removeMetadataEntry = (entryId: string): void => {
    onChange({
      ...row,
      metadata: row.metadata.filter((entry) => entry.id !== entryId),
    });
  };

  const addMetadataEntry = (): void => {
    onChange({
      ...row,
      metadata: [...row.metadata, newMetadataEntry()],
    });
  };

  return (
    <Paper withBorder p="sm" radius="sm">
      <Stack gap="xs">
        <Group justify="space-between" align="center">
          <Group gap="xs" align="center">
            <Badge size="sm" variant="light" color="blue">
              Team override #{index + 1}
            </Badge>
          </Group>
          <ActionIcon
            color="red"
            variant="subtle"
            onClick={onRemove}
            aria-label="Remove team override"
          >
            <IconTrash size={14} />
          </ActionIcon>
        </Group>
        <NumberInput
          label="Position (1-based)"
          description={`1..${maxPosition} into the synthetic team list. Leave empty to skip.`}
          placeholder="e.g. 1"
          min={1}
          max={maxPosition}
          step={1}
          allowDecimal={false}
          value={row.position ?? ""}
          onChange={(v) => {
            const raw = numberInputToNullable(v);
            const clamped =
              raw === null
                ? null
                : Math.min(maxPosition, Math.max(1, Math.trunc(raw)));
            onChange({ ...row, position: clamped });
          }}
        />
        <Text size="xs" c="dimmed" fw={600} tt="uppercase">
          Metadata bag
        </Text>
        {row.metadata.length === 0 ? (
          <Text size="xs" c="dimmed" fs="italic">
            No metadata entries yet. Add one to seed values like{" "}
            <Text span ff="monospace">
              lgts_points
            </Text>
            .
          </Text>
        ) : (
          <Table withTableBorder withColumnBorders verticalSpacing={4}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Key</Table.Th>
                <Table.Th>Value</Table.Th>
                <Table.Th style={{ width: 40 }} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {row.metadata.map((entry) => {
                const isEmptyKey = validation.emptyKeyEntryIds.has(entry.id);
                const isDuplicateKey = validation.duplicateKeyEntryIds.has(
                  entry.id,
                );
                const keyError = isEmptyKey
                  ? "Key is required"
                  : isDuplicateKey
                    ? "Duplicate key"
                    : undefined;
                return (
                  <Table.Tr key={entry.id}>
                    <Table.Td>
                      <TextInput
                        size="xs"
                        placeholder="lgts_points"
                        value={entry.key}
                        error={keyError}
                        onChange={(e) =>
                          updateMetadataEntry(entry.id, {
                            key: e.currentTarget.value,
                          })
                        }
                      />
                    </Table.Td>
                    <Table.Td>
                      <TextInput
                        size="xs"
                        placeholder="10"
                        value={entry.value}
                        onChange={(e) =>
                          updateMetadataEntry(entry.id, {
                            value: e.currentTarget.value,
                          })
                        }
                      />
                    </Table.Td>
                    <Table.Td>
                      <ActionIcon
                        color="red"
                        variant="subtle"
                        size="sm"
                        onClick={() => removeMetadataEntry(entry.id)}
                        aria-label="Remove metadata entry"
                      >
                        <IconTrash size={12} />
                      </ActionIcon>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        )}
        <Group>
          <Button
            size="xs"
            variant="light"
            leftSection={<IconPlus size={12} />}
            onClick={addMetadataEntry}
          >
            Add metadata key
          </Button>
        </Group>
      </Stack>
    </Paper>
  );
};

export const SimulationPanel = ({
  script,
  context,
  stepId,
  parametersSchema,
}: SimulationPanelProps) => {
  const initialValues = useMemo(
    () => buildInitialValues(parametersSchema),
    [parametersSchema],
  );
  const [values, setValues] = useState<ParameterValues>(initialValues);
  const [simulatorOverrides, setSimulatorOverrides] =
    useState<SimulatorContextOverrides>(EMPTY_SIMULATOR_OVERRIDES);
  const [teamRows, setTeamRows] = useState<TeamOverrideFormState[]>([]);
  const [teamOverridesOpen, setTeamOverridesOpen] = useState<boolean>(false);

  // Reset values when schema changes.
  useEffect(() => {
    setValues(initialValues);
  }, [initialValues]);

  // `simulateScript` is a query, but we only want to run it on demand and
  // bypass Apollo's cache so the user always sees a fresh run when they
  // re-click "Simulate" with the same inputs.
  const [runSimulate, { data, loading }] = useLazyQuery<
    SimulateScriptResponse,
    SimulateScriptVariables
  >(SIMULATE_SCRIPT_QUERY, {
    fetchPolicy: "network-only",
  });

  const updateValue = useCallback((name: string, value: ParameterValue) => {
    setValues((prev) => ({ ...prev, [name]: value }));
  }, []);

  // Effective max position: when the user overrode `teamCount`, use that;
  // otherwise the simulator default of 8.
  const effectiveTeamCount: number =
    simulatorOverrides.teamCount ?? TEAM_COUNT_DEFAULT;

  // Per-row validation, recomputed on every render. Cheap enough — bounded
  // by team-row count × metadata-entries-per-row.
  const teamRowValidations: ReadonlyMap<string, TeamOverrideRowValidation> =
    useMemo(() => {
      const map = new Map<string, TeamOverrideRowValidation>();
      for (const row of teamRows) {
        map.set(row.id, validateTeamRow(row));
      }
      return map;
    }, [teamRows]);

  const hasTeamRowErrors: boolean = useMemo(() => {
    for (const validation of teamRowValidations.values()) {
      if (
        validation.emptyKeyEntryIds.size > 0 ||
        validation.duplicateKeyEntryIds.size > 0
      ) {
        return true;
      }
    }
    return false;
  }, [teamRowValidations]);

  const buildOverridesPayload =
    useCallback((): SimulationContextOverridesInput | null => {
      const presetParameters: SimulationPresetParameterInput[] = (
        parametersSchema ?? []
      ).map((def) => toSimulationPresetParameterInput(def, values[def.name]));

      const teams: SimulationTeamOverrideInput[] = teamRows.map(
        toSimulationTeamOverrideInput,
      );

      const payload: SimulationContextOverridesInput = {
        teamCount: simulatorOverrides.teamCount,
        bestOf: simulatorOverrides.bestOf,
        // Empty arrays are sent as-is only when the user actually has zero
        // entries — never inject default seeded teams from the client.
        teams: teams.length > 0 ? teams : null,
        presetParameters:
          presetParameters.length > 0 ? presetParameters : null,
      };

      // If every field is empty, send `null` so the backend uses its full
      // default fixture without any overlays.
      if (
        payload.teamCount === null &&
        payload.bestOf === null &&
        payload.teams === null &&
        payload.presetParameters === null
      ) {
        return null;
      }
      return payload;
    }, [parametersSchema, simulatorOverrides, teamRows, values]);

  const handleSimulate = useCallback(async () => {
    if (hasTeamRowErrors) {
      notifications.show({
        title: "Fix synthetic team overrides",
        message: "Resolve duplicate or empty metadata keys before simulating.",
        color: "red",
      });
      return;
    }

    const overrides = buildOverridesPayload();

    try {
      const result = await runSimulate({
        variables: {
          input: {
            script,
            context,
            overrides,
            stepId: stepId ?? null,
          },
        },
      });
      if (result.error) {
        throw result.error;
      }
    } catch (err) {
      notifications.show({
        title: "Simulation failed",
        message: err instanceof Error ? err.message : "Unknown error",
        color: "red",
      });
    }
  }, [
    runSimulate,
    script,
    context,
    stepId,
    hasTeamRowErrors,
    buildOverridesPayload,
  ]);

  const result = data?.simulateScript;

  const addTeamRow = useCallback((): void => {
    setTeamRows((prev) => [...prev, newTeamOverrideRow()]);
  }, []);

  const updateTeamRow = useCallback((next: TeamOverrideFormState): void => {
    setTeamRows((prev) => prev.map((r) => (r.id === next.id ? next : r)));
  }, []);

  const removeTeamRow = useCallback((rowId: string): void => {
    setTeamRows((prev) => prev.filter((r) => r.id !== rowId));
  }, []);

  return (
    <Stack gap="sm">
      <Group justify="space-between" align="center">
        <Title order={5}>Script simulation</Title>
        <Group gap="xs">
          {loading && <Loader size="xs" />}
          <Button
            size="xs"
            leftSection={<IconPlayerPlay size={14} />}
            onClick={() => {
              void handleSimulate();
            }}
            disabled={
              loading || script.trim().length === 0 || hasTeamRowErrors
            }
            loading={loading}
          >
            Simulate
          </Button>
        </Group>
      </Group>

      <Paper withBorder p="sm" radius="sm">
        <Stack gap="xs">
          <Text size="xs" c="dimmed" fw={600} tt="uppercase">
            Simulation context
          </Text>
          <NumberInput
            label="Team count (simulator only)"
            description="Resizes the synthetic step roster used for previews. Defaults to 8."
            placeholder="8"
            min={TEAM_COUNT_MIN}
            max={TEAM_COUNT_MAX}
            step={TEAM_COUNT_STEP}
            allowDecimal={false}
            value={simulatorOverrides.teamCount ?? ""}
            onChange={(v) => {
              const raw = numberInputToNullable(v);
              setSimulatorOverrides((prev) => ({
                ...prev,
                teamCount: raw === null ? null : clampTeamCount(raw),
              }));
            }}
          />
          <NumberInput
            label="Best-of (simulator only)"
            description="Number of matches per game in the synthetic bracket. Defaults to 1."
            placeholder="1"
            min={BEST_OF_MIN}
            max={BEST_OF_MAX}
            step={1}
            allowDecimal={false}
            value={simulatorOverrides.bestOf ?? ""}
            onChange={(v) => {
              const raw = numberInputToNullable(v);
              setSimulatorOverrides((prev) => ({
                ...prev,
                bestOf: raw === null ? null : clampBestOf(raw),
              }));
            }}
          />
        </Stack>
      </Paper>

      <Paper withBorder p="sm" radius="sm">
        <Stack gap="xs">
          <Group justify="space-between" align="center">
            <Group gap="xs" align="center">
              <IconUsersGroup
                size={16}
                color="var(--mantine-color-blue-6)"
              />
              <Text size="xs" c="dimmed" fw={600} tt="uppercase">
                Synthetic team overrides
              </Text>
              {teamRows.length > 0 && (
                <Badge variant="light" size="xs" color="blue">
                  {teamRows.length}
                </Badge>
              )}
            </Group>
            <Button
              size="xs"
              variant="subtle"
              onClick={() => setTeamOverridesOpen((prev) => !prev)}
              aria-expanded={teamOverridesOpen}
              aria-controls="simulation-team-overrides-panel"
            >
              {teamOverridesOpen ? "Hide" : "Show"}
            </Button>
          </Group>
          <Text size="xs" c="dimmed">
            Seed metadata on individual synthetic teams so sort-by-metadata
            scripts produce meaningful previews instead of all-tied results
            from empty bags.
          </Text>
          <Collapse in={teamOverridesOpen} id="simulation-team-overrides-panel">
            <Stack gap="xs">
              {teamRows.map((row, idx) => {
                const validation = teamRowValidations.get(row.id);
                if (validation === undefined) {
                  throw new Error(
                    `Missing validation for team row ${row.id}`,
                  );
                }
                return (
                  <TeamOverrideRow
                    key={row.id}
                    row={row}
                    index={idx}
                    maxPosition={effectiveTeamCount}
                    validation={validation}
                    onChange={updateTeamRow}
                    onRemove={() => removeTeamRow(row.id)}
                  />
                );
              })}
              <Group>
                <Button
                  size="xs"
                  variant="light"
                  leftSection={<IconPlus size={12} />}
                  onClick={addTeamRow}
                >
                  Add team override
                </Button>
              </Group>
            </Stack>
          </Collapse>
        </Stack>
      </Paper>

      {parametersSchema && parametersSchema.length > 0 && (
        <Paper withBorder p="sm" radius="sm">
          <Stack gap="xs">
            <Text size="xs" c="dimmed" fw={600} tt="uppercase">
              Parameters
            </Text>
            {parametersSchema.map((def) => {
              const value = values[def.name];
              const label = def.required ? `${def.name} *` : def.name;
              const description = def.description ?? undefined;

              if (
                def.type === ScriptParameterType.INT ||
                def.type === ScriptParameterType.FLOAT
              ) {
                return (
                  <NumberInput
                    key={def.name}
                    label={label}
                    description={description}
                    value={typeof value === "number" ? value : 0}
                    onChange={(v) =>
                      updateValue(def.name, typeof v === "number" ? v : 0)
                    }
                    allowDecimal={def.type === ScriptParameterType.FLOAT}
                  />
                );
              }

              if (def.type === ScriptParameterType.STRING) {
                return (
                  <TextInput
                    key={def.name}
                    label={label}
                    description={description}
                    value={typeof value === "string" ? value : ""}
                    onChange={(e) =>
                      updateValue(def.name, e.currentTarget.value)
                    }
                  />
                );
              }

              return (
                <Switch
                  key={def.name}
                  label={label}
                  description={description}
                  checked={value === true}
                  onChange={(e) =>
                    updateValue(def.name, e.currentTarget.checked)
                  }
                />
              );
            })}
          </Stack>
        </Paper>
      )}

      {result && (
        <Stack gap="xs">
          <Group gap="xs">
            <Badge
              color={result.success ? "green" : "red"}
              size="lg"
              variant="light"
              leftSection={
                result.success ? (
                  <IconCircleCheck size={12} />
                ) : (
                  <IconCircleX size={12} />
                )
              }
            >
              {result.success ? "Success" : "Failed"}
            </Badge>
            <Badge
              variant="light"
              color="gray"
              leftSection={<IconClock size={12} />}
            >
              {result.executionTimeMs} ms
            </Badge>
            <Badge
              variant="light"
              color="gray"
              leftSection={<IconBolt size={12} />}
            >
              {result.instructionsUsed} instr.
            </Badge>
          </Group>

          <ScriptIssueList
            issues={result.errors}
            severity="error"
            keyPrefix="simulation-error"
          />
          <ScriptIssueList
            issues={result.warnings}
            severity="warning"
            keyPrefix="simulation-warning"
          />

          {result.effectsSummary.length > 0 && (
            <>
              <Divider
                label={`Effects (${result.effectsSummary.length})`}
                labelPosition="left"
              />
              <EffectsList
                effects={result.effectsSummary}
                variant="success"
              />
            </>
          )}

          {result.partialEffectsSummary &&
            result.partialEffectsSummary.length > 0 && (
              <>
                <Divider
                  label="Effects collected before error"
                  labelPosition="left"
                />
                <Text size="xs" c="dimmed" fs="italic">
                  Diagnostic only — none of these effects were applied.
                </Text>
                <EffectsList
                  effects={result.partialEffectsSummary}
                  variant="partial"
                />
              </>
            )}

          {result.success &&
            result.effectsSummary.length === 0 &&
            result.errors.length === 0 &&
            result.warnings.length === 0 && (
              <Text size="sm" c="dimmed">
                Script ran successfully with no recorded effects.
              </Text>
            )}
        </Stack>
      )}
    </Stack>
  );
};
